import { useState } from "react";

type Screen = "signin" | "tickets";

const placeholderTickets = [
  {
    id: 2048,
    subject: "Refund request — linen set",
    status: "Agent is looking into it",
    lastUpdate: "about an hour ago",
  },
  {
    id: 1997,
    subject: "Question about sizing",
    status: "Resolved",
    lastUpdate: "2 weeks ago",
  },
];

export default function App() {
  const [screen, setScreen] = useState<Screen>("signin");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    // Real magic-link flow lands with M5 (customers service) + M7 (email).
    // Until then, simulate: wait a moment then drop the demo user into the
    // ticket list so the UI shape can be reviewed.
    setTimeout(() => setScreen("tickets"), 1500);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm font-semibold tracking-tight text-ink-900">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white text-xs">
              HD
            </span>
            <span>Acme Retail · Support</span>
          </div>
          {screen === "tickets" && (
            <button
              type="button"
              onClick={() => {
                setScreen("signin");
                setSent(false);
                setEmail("");
              }}
              className="text-sm font-medium text-ink-600 hover:text-ink-900"
            >
              Sign out
            </button>
          )}
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          {screen === "signin" ? (
            <SignIn
              email={email}
              setEmail={setEmail}
              onSubmit={onSubmit}
              sent={sent}
            />
          ) : (
            <TicketList email={email || "you@example.com"} />
          )}
        </div>
      </main>

      <footer className="border-t border-ink-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-4 text-xs text-ink-500 sm:px-6">
          Portal powered by Helpdesk CRM — early preview
        </div>
      </footer>
    </div>
  );
}

function SignIn({
  email,
  setEmail,
  onSubmit,
  sent,
}: {
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  sent: boolean;
}) {
  return (
    <div className="mx-auto max-w-md">
      <h1 className="text-3xl font-semibold tracking-tight text-ink-900">
        Sign in to see your tickets
      </h1>
      <p className="mt-2 text-sm text-ink-600">
        Enter the email address you used when contacting support. We'll send
        you a one-time sign-in link. No password to remember.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-3">
        <label className="block">
          <span className="text-sm font-medium text-ink-700">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 block w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            disabled={sent}
          />
        </label>
        <button
          type="submit"
          disabled={sent}
          className="w-full rounded-md bg-ink-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-ink-800 disabled:opacity-60"
        >
          {sent ? "Sending link…" : "Send me a sign-in link"}
        </button>
      </form>

      {sent && (
        <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 p-3 text-sm text-brand-800">
          <strong>Preview mode:</strong> pretend the email landed. Dropping you
          into your tickets in a moment.
        </div>
      )}

      <p className="mt-8 text-xs text-ink-500">
        This is a placeholder sign-in. Real magic-link email ships with
        milestone M7 (email-gateway). Until then, any email works — data on
        the next screen is fake.
      </p>
    </div>
  );
}

function TicketList({ email }: { email: string }) {
  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-900">
            Your support tickets
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Signed in as <span className="font-medium text-ink-700">{email}</span>
          </p>
        </div>
        <button
          type="button"
          disabled
          title="New ticket UI ships with M9"
          className="rounded-md bg-ink-900 px-3.5 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          + New ticket
        </button>
      </div>

      <ul className="divide-y divide-ink-200 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm">
        {placeholderTickets.map((t) => (
          <li key={t.id} className="flex items-start gap-4 p-4 hover:bg-ink-50">
            <div className="mt-1 h-2 w-2 flex-none rounded-full bg-brand-500" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-ink-500">
                  #{t.id}
                </span>
                <span className="truncate text-sm font-semibold text-ink-900">
                  {t.subject}
                </span>
              </div>
              <p className="mt-1 text-sm text-ink-600">{t.status}</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Last update {t.lastUpdate}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-8 text-xs text-ink-500">
        Placeholder data. Real tickets load once the customers + ticketing
        services are wired (M5 + M6).
      </p>
    </>
  );
}
